import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { History, RefreshCw, XCircle } from "lucide-react";
import { Card } from "components/ui/Card";
import { Button } from "components/ui/Button";
import { Badge } from "components/ui/Badge";
import { Modal } from "components/ui/Modal";
import { EmptyState } from "components/ui/EmptyState";
import { Skeleton } from "components/ui/Skeleton";
import { showToast } from "utils/showToast";
import { cn } from "utils/cn";
import {
  cancelWhatsappBroadcast,
  getWhatsappBroadcast,
  getWhatsappBroadcasts,
} from "api/whatsappBroadcasts";
import { isBroadcastRunning } from "./broadcastMessage";

/** Cada cuánto se refresca el progreso de una campaña en curso. */
export const PROGRESS_POLL_MS = 10_000;

const STATUS_BADGE = {
  preparing: { tone: "neutral", labelKey: "Preparando" },
  active: { tone: "brand", labelKey: "En curso" },
  completed: { tone: "success", labelKey: "Terminada" },
  cancelled: { tone: "neutral", labelKey: "Cancelada" },
  failed: { tone: "error", labelKey: "Detenida" },
};

const WAITING_REASON_KEYS = {
  outside_window: "Fuera del horario de envío (09:00 a 23:00). Se retoma automáticamente.",
  quota: "Se alcanzó el cupo diario del número. Se retoma mañana automáticamente.",
  instance_not_connected: "El número de WhatsApp está desconectado. Reconéctalo para que siga el envío.",
};

const END_REASON_KEYS = {
  integration_not_found: "El número de WhatsApp ya no existe para este operador.",
  integration_purpose_not_allowed: "El número quedó configurado solo para la Mesa de ayuda.",
  cancelled_by_user: "Cancelada por un usuario.",
  expired: "Venció a los 7 días: lo pendiente no se envió.",
  creation_interrupted: "La creación se interrumpió; no se envió nada.",
  enqueue_failed: "No se pudo encolar; no se envió nada.",
};

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const config = STATUS_BADGE[status] || STATUS_BADGE.preparing;
  return <Badge tone={config.tone}>{t(config.labelKey)}</Badge>;
}

const failuresOf = (counts) =>
  (counts?.failed || 0) + (counts?.notOnWhatsapp || 0) + (counts?.unknown || 0) + (counts?.expired || 0);

/**
 * Progreso de la campaña elegida (se refresca solo mientras avanza) e historial de las
 * comunicaciones del operador.
 *
 * Props:
 *   activeId      id de la campaña a mostrar (la recién creada o la elegida del historial)
 *   onSelect      (id) => void
 *   refreshToken  cambia cuando se crea una campaña: recarga el historial
 */
export default function BroadcastActivity({ activeId, onSelect, refreshToken }) {
  const { t } = useTranslation();
  const [history, setHistory] = useState({ items: [], loading: true, error: null });
  const [detail, setDetail] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadHistory = useCallback(async () => {
    const response = await getWhatsappBroadcasts({ page: 1, limit: 10 });
    if (!mounted.current) return;
    setHistory(
      response.success
        ? { items: response.data?.items || [], loading: false, error: null }
        : { items: [], loading: false, error: response.error?.message }
    );
  }, []);

  const loadDetail = useCallback(async (id) => {
    const response = await getWhatsappBroadcast(id);
    if (!mounted.current) return null;
    if (response.success) setDetail(response.data);
    return response.success ? response.data : null;
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, refreshToken]);

  // Progreso: se consulta al elegir una campaña y cada PROGRESS_POLL_MS mientras avance.
  useEffect(() => {
    if (!activeId) {
      setDetail(null);
      return undefined;
    }
    let timer = null;
    let cancelled = false;
    const tick = async () => {
      const data = await loadDetail(activeId);
      if (cancelled) return;
      if (data && isBroadcastRunning(data.status)) {
        timer = setTimeout(tick, PROGRESS_POLL_MS);
      } else if (data) {
        loadHistory();
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activeId, loadDetail, loadHistory]);

  const handleCancel = async () => {
    setCancelling(true);
    const response = await cancelWhatsappBroadcast(activeId);
    setCancelling(false);
    setConfirmCancel(false);
    if (!response.success) {
      showToast(response.error?.message || t("No se pudo cancelar la comunicación."), false);
      return;
    }
    setDetail(response.data);
    showToast(t("Se cancelaron los envíos pendientes."), true);
    loadHistory();
  };

  const counts = detail?.counts;
  const total = detail?.totalRecipients || 0;
  const done = (counts?.sent || 0) + failuresOf(counts) + (counts?.skipped || 0) + (counts?.cancelled || 0);
  const percent = total ? Math.min(100, Math.round((done / total) * 100)) : 0;

  return (
    <div className="flex flex-col gap-5">
      {detail && (
        <Card className="flex flex-col gap-3" aria-live="polite">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-fg">{t("Progreso del envío")}</h3>
              <StatusBadge status={detail.status} />
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => loadDetail(activeId)}
                iconLeft={<RefreshCw className="h-4 w-4" />}
              >
                {t("Actualizar")}
              </Button>
              {isBroadcastRunning(detail.status) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmCancel(true)}
                  iconLeft={<XCircle className="h-4 w-4" />}
                >
                  {t("Cancelar pendientes")}
                </Button>
              )}
            </div>
          </div>

          <div
            className="h-2 w-full overflow-hidden rounded-full bg-bg-muted"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            aria-label={t("Avance del envío")}
          >
            <div className="h-full rounded-full bg-brand-500 transition-all" style={{ width: `${percent}%` }} />
          </div>

          <dl className="m-0 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            {[
              { label: t("Enviados"), value: counts?.sent || 0, tone: "text-status-success" },
              { label: t("Pendientes"), value: (counts?.pending || 0) + (counts?.sending || 0), tone: "text-fg" },
              { label: t("Sin WhatsApp / fallidos"), value: failuresOf(counts), tone: "text-status-error" },
              { label: t("Omitidos o cancelados"), value: (counts?.skipped || 0) + (counts?.cancelled || 0), tone: "text-fg-muted" },
            ].map((item) => (
              <div key={item.label} className="flex flex-col">
                <dt className="text-xs text-fg-muted">{item.label}</dt>
                <dd className={cn("m-0 text-lg font-semibold tabular-nums", item.tone)}>{item.value}</dd>
              </div>
            ))}
          </dl>

          {detail.waitingReason && (
            <p className="m-0 rounded-lg bg-status-warning-soft px-3 py-2 text-sm text-status-warning">
              {t(WAITING_REASON_KEYS[detail.waitingReason])}
            </p>
          )}
          {detail.endReason && END_REASON_KEYS[detail.endReason] && (
            <p className="m-0 text-sm text-fg-muted">{t(END_REASON_KEYS[detail.endReason])}</p>
          )}
          {counts?.skipped > 0 && (
            <p className="m-0 text-xs text-fg-muted">
              {t("Omitidos: el paquete cambió de estado (por ejemplo, ya se entregó) antes del envío.")}
            </p>
          )}
        </Card>
      )}

      <Card className="flex flex-col gap-3">
        <h3 className="flex items-center gap-2 text-base font-semibold text-fg">
          <History className="h-4 w-4 text-fg-muted" />
          {t("Comunicaciones recientes")}
        </h3>
        {history.loading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : history.error ? (
          <p className="m-0 text-sm text-status-error" role="alert">
            {history.error}
          </p>
        ) : !history.items.length ? (
          <EmptyState
            icon={<History />}
            title={t("Sin comunicaciones")}
            description={t("Las comunicaciones que envíes aparecerán acá.")}
          />
        ) : (
          <ul className="m-0 flex list-none flex-col p-0">
            {history.items.map((item) => (
              <li key={item.id} className="border-b border-border last:border-b-0">
                <button
                  type="button"
                  onClick={() => onSelect(item.id)}
                  className={cn(
                    "flex w-full flex-wrap items-center justify-between gap-2 px-1 py-2.5 text-left text-sm hover:bg-bg-muted",
                    item.id === activeId && "bg-bg-muted"
                  )}
                >
                  <span className="flex items-center gap-2">
                    <StatusBadge status={item.status} />
                    <span className="text-fg">
                      {item.mode === "orden_demorada" ? t("Orden demorada") : t("Texto libre")}
                    </span>
                  </span>
                  <span className="text-xs text-fg-muted">
                    {t("{{sent}} de {{total}} enviados", { sent: item.counts?.sent || 0, total: item.totalRecipients })}
                    {" · "}
                    {item.createdAt ? new Date(item.createdAt).toLocaleString() : "—"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        size="sm"
        title={t("¿Cancelar los envíos pendientes?")}
        description={t("Los mensajes que ya salieron no se pueden deshacer. Lo pendiente no se enviará.")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmCancel(false)} disabled={cancelling}>
              {t("Volver")}
            </Button>
            <Button onClick={handleCancel} loading={cancelling} disabled={cancelling}>
              {t("Cancelar pendientes")}
            </Button>
          </>
        }
      />
    </div>
  );
}
